import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import Toast from '../components/Toast';
import { ProductsProvider } from '../context/ProductsContext';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../context/OrdersContext';
import { useThermalPrinter } from '../context/ThermalPrinterContext';
import { formatCurrency } from '../data/mockData';
import { startOfflineReplay } from '../utils/posOfflineQueue';
import styles from './AppLayout.module.css';

function OfflineReplayHost() {
  const { user } = useAuth();
  const { notify } = useOrders();
  const { connected, printReceipt } = useThermalPrinter();
  const token = user?.idToken;

  useEffect(() => {
    if (!token) return undefined;
    return startOfflineReplay({
      getToken: () => token,
      onEvent: (event) => {
        if (event.type === 'charge-synced' && event.receipt?.display_id) {
          const method = String(event.paymentMethod || 'PAY_LATER').toUpperCase();
          const label = method === 'PAY_LATER' ? 'Pay Later' : method;
          const variance = Number(event.variance) || 0;
          notify(variance
            ? `${label} synced · ${event.receipt.display_id} · collected ${formatCurrency(event.collectedAmount)} · server ${formatCurrency(event.serverTotal)}`
            : `${label} synced · ${event.receipt.display_id}`);
          if (connected && event.receipt) {
            printReceipt(event.receipt).catch(() => {});
          }
        } else if (event.type === 'conflict') {
          notify('A queued edit was not saved. The original bill is on the server.');
        } else if (event.type === 'already-collected') {
          notify('Already collected.');
        } else if (event.type === 'settle-synced') {
          notify('Pay Later collection synced.');
        }
      },
    });
  }, [token, connected, notify, printReceipt]);

  return null;
}

export default function AppLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [navOpen]);

  return (
    <ProductsProvider>
      <div className={styles.shell}>
        {navOpen && (
          <button
            type="button"
            className={styles.navScrim}
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
          />
        )}
        <Sidebar mobileOpen={navOpen} onMobileClose={() => setNavOpen(false)} />
        <div className={styles.main}>
          <Topbar onMenuClick={() => setNavOpen(true)} />
          <div className={styles.content}>
            <Outlet />
          </div>
        </div>
        <Toast />
        <OfflineReplayHost />
      </div>
    </ProductsProvider>
  );
}
